/**
 * Visitor tracking and persistence utilities.
 *
 * Generates a unique visitor ID stored in localStorage that persists
 * across sessions. Also handles customer info persistence.
 */

const VISITOR_ID_KEY = 'hwg_visitor_id';
const CUSTOMER_INFO_KEY = 'hwg_customer_info';
const SESSION_COUNT_KEY = 'hwg_session_count';
const FINGERPRINT_KEY = 'hwg_device_fingerprint';
const RESEARCH_MODE_KEY = 'hwg_research_mode';
const MESSAGES_KEY = 'hwg_messages';

export interface PersistedCustomerInfo {
  email?: string;
  name?: string;
  country?: string;
  phone?: string;
  anonymous: boolean;
  emailVerified?: boolean;  // Research mode email verification status
  lastUpdated?: string;
}

export interface VisitorInfo {
  visitorId: string;
  sessionCount: number;
  firstSeen: string;
  lastSeen: string;
}

export interface DeviceFingerprint {
  visitorId: string;
  confidence?: number;
  collectedAt: string;
}

/**
 * Generate a unique visitor ID (UUID v4 format)
 */
function generateVisitorId(): string {
  // Use crypto.randomUUID if available, otherwise fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create visitor ID from localStorage
 */
export function getVisitorId(): string {
  try {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = generateVisitorId();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
  } catch {
    // localStorage not available (private browsing, etc.)
    return generateVisitorId();
  }
}

/**
 * Get visitor info including session count
 */
export function getVisitorInfo(): VisitorInfo {
  const visitorId = getVisitorId();
  const now = new Date().toISOString();

  try {
    const sessionData = localStorage.getItem(SESSION_COUNT_KEY);
    let sessionCount = 1;
    let firstSeen = now;

    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      sessionCount = (parsed.count || 0) + 1;
      firstSeen = parsed.firstSeen || now;
    }

    // Update session data
    localStorage.setItem(SESSION_COUNT_KEY, JSON.stringify({
      count: sessionCount,
      firstSeen,
      lastSeen: now,
    }));

    return {
      visitorId,
      sessionCount,
      firstSeen,
      lastSeen: now,
    };
  } catch {
    return {
      visitorId,
      sessionCount: 1,
      firstSeen: now,
      lastSeen: now,
    };
  }
}

/**
 * Get persisted customer info from localStorage
 */
export function getPersistedCustomer(): PersistedCustomerInfo | null {
  try {
    const stored = localStorage.getItem(CUSTOMER_INFO_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Persist customer info to localStorage
 */
export function persistCustomer(customer: PersistedCustomerInfo): void {
  try {
    localStorage.setItem(CUSTOMER_INFO_KEY, JSON.stringify({
      ...customer,
      lastUpdated: new Date().toISOString(),
    }));
  } catch {
    // localStorage not available
  }
}

/**
 * Clear persisted customer info (e.g., on logout)
 */
export function clearPersistedCustomer(): void {
  try {
    localStorage.removeItem(CUSTOMER_INFO_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Store device fingerprint
 */
export function storeFingerprint(fingerprintId: string): void {
  try {
    const fingerprint: DeviceFingerprint = {
      visitorId: fingerprintId,
      collectedAt: new Date().toISOString(),
    };
    localStorage.setItem(FINGERPRINT_KEY, JSON.stringify(fingerprint));
  } catch {
    // localStorage not available
  }
}

/**
 * Get stored device fingerprint
 */
export function getStoredFingerprint(): DeviceFingerprint | null {
  try {
    const stored = localStorage.getItem(FINGERPRINT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore
  }
  return null;
}

// ============================================================================
// Research Mode Persistence
// ============================================================================

export interface PersistedResearchMode {
  enabled: boolean;
  threadId?: string;
  enabledAt?: string;
}

/**
 * Get persisted research mode state
 */
export function getPersistedResearchMode(): PersistedResearchMode | null {
  try {
    const stored = localStorage.getItem(RESEARCH_MODE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Persist research mode state
 */
export function persistResearchMode(state: PersistedResearchMode): void {
  try {
    localStorage.setItem(RESEARCH_MODE_KEY, JSON.stringify(state));
  } catch {
    // localStorage not available
  }
}

/**
 * Clear research mode state
 */
export function clearPersistedResearchMode(): void {
  try {
    localStorage.removeItem(RESEARCH_MODE_KEY);
  } catch {
    // Ignore
  }
}

// ============================================================================
// Message Persistence (for research mode sessions)
// ============================================================================

export interface PersistedMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'edge_llm';
  content: string;
  timestamp: string;
  type?: string;
  metadata?: Record<string, unknown>;
}

export interface PersistedMessages {
  threadId: string;
  messages: PersistedMessage[];
  lastUpdated: string;
}

/**
 * Get persisted messages for research mode session
 */
export function getPersistedMessages(): PersistedMessages | null {
  try {
    const stored = localStorage.getItem(MESSAGES_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Persist messages for research mode session
 */
export function persistMessages(threadId: string, messages: PersistedMessage[]): void {
  try {
    const data: PersistedMessages = {
      threadId,
      messages,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(data));
  } catch {
    // localStorage not available
  }
}

/**
 * Clear persisted messages
 */
export function clearPersistedMessages(): void {
  try {
    localStorage.removeItem(MESSAGES_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Clear all research mode data (messages + state)
 * Called when user disables research mode
 */
export function clearResearchModeData(): void {
  clearPersistedResearchMode();
  clearPersistedMessages();
}
